import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';

// Define the Media interface
interface Media {
  id: number;
  file_name: string;
  file_type: string;
  file_size?: string;
  temporaryUrl: string;
  is_legacy: number;
  created_at?: string;
  recipients?: any[];
}

// Define the props interface
interface MediaGridWithPaginationProps {
  media: Media[];
  loading: boolean;
  activeFilter: string;
  getMediaCard: (item: Media) => React.ReactNode;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  isServerPagination?: boolean;
}

// This is the updated section with pagination
const MediaGridWithPagination: React.FC<MediaGridWithPaginationProps> = ({ 
  media, 
  loading, 
  activeFilter, 
  getMediaCard,
  currentPage: externalCurrentPage,
  totalPages: externalTotalPages,
  onPageChange,
  isServerPagination = true
}) => {
  const [currentPage, setCurrentPage] = useState<number>(externalCurrentPage || 1);
  const [totalPages, setTotalPages] = useState<number>(externalTotalPages || 1);
  const [paginatedMedia, setPaginatedMedia] = useState<Media[]>([]);
  const itemsPerPage = 8; // You can adjust this number based on your preference


//   console?.log(currentPage)

  useEffect(() => {
    if (isServerPagination) {
      // For server-side pagination, use the data as is
      setPaginatedMedia(media);
      
      // Update internal states from props if they are provided
      if (externalCurrentPage) setCurrentPage(externalCurrentPage);
      if (externalTotalPages) setTotalPages(externalTotalPages);
    } else {
      // For client-side pagination, filter and paginate the data
      const filteredMedia = media.filter(
        (item) => activeFilter === 'all' || item.file_type === activeFilter
      );

      // Calculate total pages
      const pages = Math.ceil(filteredMedia.length / itemsPerPage);
      setTotalPages(pages || 1);

      // Reset to page 1 when filter changes
      if (currentPage > pages) {
        setCurrentPage(1);
      }

      // Get media for current page
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setPaginatedMedia(filteredMedia.slice(startIndex, endIndex));
    }
  }, [media, activeFilter, currentPage, isServerPagination, externalCurrentPage, externalTotalPages]);

  // Handle page change
  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    
    // If using server-side pagination, call the parent callback
    if (isServerPagination && onPageChange) {
      onPageChange(page);
    }
    
    // Scroll to top of media section
    const mediaGridElement = document.getElementById('media-grid');
    if (mediaGridElement) {
      window.scrollTo({ 
        top: mediaGridElement.offsetTop - 100, 
        behavior: 'smooth' 
      });
    }
  };

  // Generate pagination links
  const renderPaginationLinks = (): React.ReactNode[] => {
    const links: React.ReactNode[] = [];
    
    // Previous page button
    links.push(
      <button 
        key="prev" 
        onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center justify-center w-10 h-10 rounded-md ${
          currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    );

    // Page numbers
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're at the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page if not visible
    if (startPage > 1) {
      links.push(
        <button
          key="1"
          onClick={() => handlePageChange(1)}
          className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-gray-100"
        >
          1
        </button>
      );
      
      // Ellipsis if needed
      if (startPage > 2) {
        links.push(
          <span key="ellipsis1" className="flex items-center justify-center w-10 h-10">
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      links.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`flex items-center justify-center w-10 h-10 rounded-md ${
            currentPage === i
              ? 'bg-orange-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page if not visible
    if (endPage < totalPages) {
      // Ellipsis if needed
      if (endPage < totalPages - 1) {
        links.push(
          <span key="ellipsis2" className="flex items-center justify-center w-10 h-10">
            ...
          </span>
        );
      }
      
      links.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-gray-100"
        >
          {totalPages}
        </button>
      );
    }

    // Next page button
    links.push(
      <button
        key="next"
        onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center justify-center w-10 h-10 rounded-md ${
          currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    );

    return links;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (media.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed rounded-lg">
        <p className="text-gray-500 text-center mb-4">No media files yet</p>
        <NavLink to={'/add-media'} className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
          Upload Media
        </NavLink>
      </div>
    );
  }

  return (
    <div>
      <div id="media-grid" className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
       
      {/* {media
                                    .filter((item) => activeFilter === 'all' || item.file_type === activeFilter)
                                    .map((item) => (
                                        <div key={item.id}
                                            className="cursor-pointer ">{getMediaCard(item)}</div>
                                    ))} */}
        {paginatedMedia.filter((item) => activeFilter === 'all' || item.file_type === activeFilter)
                                    .map((item) => (
          <div key={item.id} className="cursor-pointer">
            {getMediaCard(item)}
          </div>
        ))}



      </div>
      
      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex justify-end mt-8 mb-4">
          <div className="flex space-x-1">
            {renderPaginationLinks()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGridWithPagination;